import type { LineGridDef } from '@project/contracts'
import type { LineRow } from '../app/api.js'
import { asText } from './format.js'

/**
 * Two grids over one storage section — the estimate under a BOQ line and the
 * common items under none — and the rows each one owns.
 */
export const gridRows = (grid: LineGridDef, sectionRows: readonly LineRow[]): LineRow[] => {
  const rule = grid.rows
  if (!rule) return [...sectionRows]
  return sectionRows.filter((l) => (asText(l[rule.column]) !== '') === rule.isSet)
}

const CODE_CHARS = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'

/** A short code nobody has to invent — the reference's `toi8hg`. */
export const shortCode = (taken: ReadonlySet<string>): string => {
  for (;;) {
    let out = ''
    for (let i = 0; i < 6; i += 1) out += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]
    if (!taken.has(out)) return out
  }
}

/**
 * What "Add" appends on a grid that shares its section or numbers its rows:
 * a seq above every row of the section (so the two grids never hand out the
 * same one, which the database refuses), and its automatic reference.
 */
export const newRowFor = (grid: LineGridDef, lines: readonly LineRow[]): LineRow => {
  const section = grid.section ?? 'ITEM'
  const inSection = lines.filter((l) => String(l['section'] ?? section) === section)
  const seq = inSection.reduce((max, l) => Math.max(max, Number(l['seq']) || 0), 0) + 1
  const row: Record<string, unknown> = { seq }
  const auto = grid.autoRef
  if (auto) {
    const refs = lines.map((l) => asText(l[auto.column])).filter((v) => v !== '')
    row[auto.column] = auto.style === 'code'
      ? shortCode(new Set(refs))
      : String(refs.reduce((max, v) => (/^\d+$/.test(v) ? Math.max(max, Number(v)) : max), 0) + 1)
  }
  return row
}
